import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { api } from '../../services/api';
import { CreditCostNotice } from '../credits/CreditCostNotice';
import { usePipelineCostItems, useStepCostItems, ESTIMATE_NOTE } from '../credits/pipelineCost';

// Uma imagem (regerar/reprocessar): estimativa do backend pelo custo de provedor.
const ONE_IMAGE_STEP = { id: 'regen', kind: 'image', title: 'Imagem' };
import { useCreditConfirm } from '../credits/useCreditConfirm';
import { PlatformIcon } from '../shared/PlatformIcon';
import { compressImageForAi, WizardHeader, WizardFooter } from './ecommerce/shared';
import { ProductSourcePicker, EMPTY_MANUAL, type ManualProduct, type ProductSource } from './ecommerce/ProductSourcePicker';
import { PipelinePicker } from './ecommerce/PipelinePicker';
import { GenerationsGallery } from './ecommerce/GenerationsGallery';
import { PhotoPipelineFlow, photoStepChip, type PhotoPipeline as Pipeline, type PhotoPipelineStep as PipelineStep } from './ecommerce/PhotoPipelineFlow';

interface PhotoJob {
  id: string;
  status: string;
  progress: number;
  currentStep?: string;
  productId?: string;
  steps: PipelineStep[];
  input?: any;
  output?: {
    results?: Array<{ stepId: string; title: string; kind: string; imageUrl?: string; prompt?: string }>;
    seo?: any[];
    _pendingRegen?: any;
    _regenError?: string;
    // Entrega parcial: o backend reprocessa as etapas que falharam e, quando ainda
    // assim alguma não sai, registra aqui o que faltou (com estorno dos créditos).
    partial?: boolean;
    failures?: Array<{ stepId: string; title: string; kind: string; message: string }>;
    summary?: { requested: number; delivered: number; failed: number };
  };
  error?: string;
  createdAt?: string;
}

const STATUS_LABEL: Record<string, string> = {
  queued: 'Na fila', running: 'Em execução', awaiting_approval: 'Aguardando aprovação',
  regenerating: 'Regerando', awaiting_adjustment: 'Aguardando aprovação de ajuste',
  completed: 'Concluído', failed: 'Falhou',
};

const IMAGE_EXTENSION_BY_MIME: Record<string, string> = {
  'image/avif': 'avif',
  'image/gif': 'gif',
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/svg+xml': 'svg',
  'image/webp': 'webp',
};

function imageDownloadFilename(filename: string | undefined, blob: Blob, url: string) {
  const mime = blob.type.toLowerCase().split(';')[0].trim();
  const mimeExtension = IMAGE_EXTENSION_BY_MIME[mime];
  let urlExtension = '';

  try {
    const match = new URL(url, window.location.href).pathname.match(/\.([a-z0-9]+)$/i);
    if (match && ['avif', 'gif', 'jpeg', 'jpg', 'png', 'svg', 'webp'].includes(match[1].toLowerCase())) {
      urlExtension = match[1].toLowerCase() === 'jpeg' ? 'jpg' : match[1].toLowerCase();
    }
  } catch {
    // A MIME type or the default PNG extension will still make the file usable.
  }

  const extension = mimeExtension || urlExtension || 'png';
  const requestedName = filename?.trim() || `imagem-${Date.now()}`;
  const safeName = requestedName
    .replace(/^.*[\\/]/, '')
    .replace(/[<>:"/\\|?*\u0000-\u001f]/g, '-')
    .replace(/[.\s]+$/, '') || `imagem-${Date.now()}`;

  const knownImageExtension = /\.(avif|gif|jpe?g|png|svg|webp)$/i;
  if (knownImageExtension.test(safeName)) {
    return safeName.replace(knownImageExtension, `.${extension}`);
  }
  return `${safeName}.${extension}`;
}

// ─── Execução / resultados do job ────────────────────────────────────────────
// O Cut App aceita no máximo 6 fotos por produto (o backend também corta).
const CUTAPP_MAX_PHOTOS = 6;

function JobRunner({ job, product, onChanged }: { job: PhotoJob; product: any; onChanged: () => void }) {
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);
  const [mode, setMode] = useState<'replace' | 'append'>('replace');
  const [updateMkt, setUpdateMkt] = useState(false);
  const [mktTargets, setMktTargets] = useState<any[]>([]);
  const [selectedTargets, setSelectedTargets] = useState<string[]>([]);
  const [targetsLoading, setTargetsLoading] = useState(false);
  const [regenStep, setRegenStep] = useState<{ id: string; title: string } | null>(null);
  const oneImageCost = useStepCostItems('photo', ONE_IMAGE_STEP);
  const [regenFeedback, setRegenFeedback] = useState('');
  const [regenRefUrl, setRegenRefUrl] = useState('');
  const [regenRefUploading, setRegenRefUploading] = useState(false);
  // Os destinos são carregados uma vez por job assim que o card "Aplicar" aparece:
  // um produto pode estar ligado só ao Cut App (sem marketplaceUrls), então só a
  // lista de targets do backend diz quais destinos realmente existem.
  const targetsLoadedFor = useRef<string | null>(null);

  const firstImageStep = (job.steps || []).find(s => s.kind === 'image');
  const firstResult = (job.output?.results || []).find(r => r.stepId === firstImageStep?.id);
  const images = (job.output?.results || []).filter(r => r.kind === 'image' && r.imageUrl);
  const seo = job.output?.seo || [];
  const linkedMarketplaces = Array.isArray(product?.marketplaceUrls)
    ? product.marketplaceUrls.filter((l: any) => l && l.platform && (l.listingId || l.url))
    : [];

  const toggle = (url: string) => setSelected(s => s.includes(url) ? s.filter(u => u !== url) : [...s, url]);

  // Pai com filhos so e trocado pelos filhos quando e agrupador (ml_family, selectable:false).
  // O pai Shopee (shopee_item_images) e o destino das fotos gerais; descarta-lo fazia a
  // Shopee nunca ser enviada, porque o unico filho (capa de variacao) fica bloqueado.
  const flattenTargets = (targets: any[]) => targets.flatMap((target: any) => {
    const children = Array.isArray(target.children) ? target.children : [];
    if (!children.length) return [target];
    return target.selectable !== false ? [target, ...children] : children;
  });
  const loadMarketplaceTargets = async () => {
    if (!job.productId) return;
    setTargetsLoading(true);
    try {
      const res = await api.listEcommerceMarketplaceTargets(job.productId);
      const targets = res.targets || [];
      setMktTargets(targets);
      setSelectedTargets(flattenTargets(targets).filter((t: any) => t.selectable !== false && (t.media || []).includes('image') && t.type !== 'shopee_model_cover').map((t: any) => t.id));
    } catch (e: any) { toast.error(e.message || 'Erro ao carregar anuncios'); }
    finally { setTargetsLoading(false); }
  };
  const toggleTarget = (id: string) => setSelectedTargets(prev => prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]);

  const canApply = job.status === 'completed' && images.length > 0 && !!job.productId;
  useEffect(() => {
    if (!canApply || !job.productId) return;
    if (targetsLoadedFor.current === job.id) return;
    targetsLoadedFor.current = job.id;
    loadMarketplaceTargets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canApply, job.id, job.productId]);

  // Anúncios de marketplace × Cut App: grupos distintos na lista de destinos.
  const marketplaceNodes = mktTargets.filter((t: any) => t.marketplace !== 'cutapp');
  const cutappNodes = mktTargets.filter((t: any) => t.marketplace === 'cutapp');
  const hasCutappTarget = cutappNodes.length > 0;
  const cutappSelected = cutappNodes.some((t: any) => selectedTargets.includes(t.id));
  const imageCount = selected.length || images.length;
  const cutappOverLimit = cutappSelected && imageCount > CUTAPP_MAX_PHOTOS;
  // Nome dos destinos para o texto do checkbox / da confirmação. O Cut App só
  // entra quando o backend devolveu um target dele para este produto.
  const destinationNames: string[] = Array.from(new Set([
    ...linkedMarketplaces.map((l: any) => l.platform),
    ...(hasCutappTarget ? ['Cut App'] : []),
  ]));

  const approve = async () => {
    setBusy(true);
    try { await api.approveEcommercePhotoJob(job.id); toast.success('Aprovado! Gerando as demais imagens...'); onChanged(); }
    catch (e: any) { toast.error(e.message || 'Erro'); } finally { setBusy(false); }
  };
  const regenerate = async () => {
    const feedback = window.prompt('Ajuste desejado na imagem (opcional):') || undefined;
    setBusy(true);
    try { await api.regenerateEcommercePhotoFirst(job.id, feedback); toast.success('Regerando imagem...'); onChanged(); }
    catch (e: any) { toast.error(e.message || 'Erro'); } finally { setBusy(false); }
  };
  const apply = async () => {
    const urls = selected.length ? selected : images.map(i => i.imageUrl!);
    if (!urls.length) { toast.error('Selecione ao menos uma imagem'); return; }
    let doMkt = updateMkt;
    if (destinationNames.length && !updateMkt) {
      const cap = hasCutappTarget && urls.length > CUTAPP_MAX_PHOTOS
        ? `\n\nO Cut App aceita no máximo ${CUTAPP_MAX_PHOTOS} fotos — só as ${CUTAPP_MAX_PHOTOS} primeiras serão enviadas para lá.`
        : '';
      doMkt = window.confirm(`Este produto está vinculado a ${destinationNames.join(', ')}. Deseja também enviar as imagens para esses destinos?${cap}`);
    }
    setBusy(true);
    try {
      const marketplaceTargets = doMkt ? flattenTargets(mktTargets).filter((target: any) => selectedTargets.includes(target.id)) : [];
      if (doMkt && !marketplaceTargets.length) { toast.error('Selecione ao menos um destino (anuncio/variacao ou Cut App).'); return; }
      const res = await api.applyEcommercePhotos(job.id, { imageUrls: urls, mode, updateMarketplaces: doMkt, marketplaceTargets });
      toast.success('Imagens aplicadas ao produto');
      if (res.marketplaceResults?.length) {
        res.marketplaceResults.forEach((r: any) => {
          if (!r.success) { toast.error(`${r.platform}: ${r.error}`); return; }
          toast.success(`${r.platform}: atualizado`);
          // Alguns destinos (ex.: Cut App, que corta em 6 fotos) devolvem um aviso
          // mesmo quando dá certo. Se o campo não vier, nada acontece.
          if (r.warning) toast(`${r.platform}: ${r.warning}`, { icon: '⚠️' });
        });
      }
      onChanged();
    } catch (e: any) { toast.error(e.message || 'Erro ao aplicar'); } finally { setBusy(false); }
  };

  const seoFor = (stepId: string) => seo.find((s: any) => s.stepId === stepId || String(s.etapa) === stepId);

  const downloadImage = async (url: string, filename?: string) => {
    try {
      const resp = await fetch(url);
      const blob = await resp.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objUrl;
      a.download = imageDownloadFilename(filename, blob, url);
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.setTimeout(() => URL.revokeObjectURL(objUrl), 1_000);
    } catch {
      window.open(url, '_blank');
    }
  };

  const uploadRegenRef = async (file: File) => {
    setRegenRefUploading(true);
    try {
      const uploadFile = await compressImageForAi(file);
      const fd = new FormData();
      fd.append('photo', uploadFile);
      const res = await api.uploadEcommerceReference(fd);
      setRegenRefUrl(res.imageUrl);
    } catch (e: any) { toast.error(e.message || 'Erro ao enviar imagem de referência'); }
    finally { setRegenRefUploading(false); }
  };

  const submitRegen = async () => {
    if (!regenStep) return;
    if (!regenFeedback.trim()) { toast.error('Descreva o ajuste desejado.'); return; }
    setBusy(true);
    try {
      await api.regenerateEcommercePhotoStep(job.id, regenStep.id, regenFeedback.trim(), regenRefUrl || undefined);
      toast.success('Regerando imagem com seu ajuste...');
      setRegenStep(null);
      setRegenFeedback('');
      setRegenRefUrl('');
      onChanged();
    } catch (e: any) { toast.error(e.message || 'Erro ao regerar'); }
    finally { setBusy(false); }
  };

  const pendingRegen = job.output?._pendingRegen || null;
  const failures = job.output?.failures || [];
  const summary = job.output?.summary || null;

  // Reprocessa uma etapa que falhou em definitivo. O crédito dela já foi
  // estornado quando o job finalizou, então esta nova tentativa é cobrada.
  const retryStep = async (stepId: string) => {
    setBusy(true);
    try {
      await api.retryEcommercePhotoStep(job.id, stepId);
      toast.success('Reprocessando a foto...');
      onChanged();
    } catch (e: any) {
      toast.error(e.message || 'Erro ao reprocessar');
    } finally { setBusy(false); }
  };
  const resolveRegen = async (accept: boolean) => {
    setBusy(true);
    try {
      await api.resolveEcommercePhotoRegen(job.id, accept);
      toast.success(accept ? 'Nova imagem definida como oficial.' : 'Mantida a imagem atual.');
      onChanged();
    } catch (e: any) { toast.error(e.message || 'Erro ao aplicar ajuste'); }
    finally { setBusy(false); }
  };

  const downloadAll = async () => {
    for (const img of images) {
      const s = seoFor(img.stepId);
      await downloadImage(img.imageUrl!, s?.nome_arquivo);
    }
  };

  const isRegenerating = job.status === 'regenerating';
  const isAwaitingAdjust = job.status === 'awaiting_adjustment';
  // Título do step em regeração (para overlay na imagem certa)
  const regenTitle = isRegenerating ? job.currentStep : null;

  return (
    <div className="space-y-4">
      {/* Barra de progresso */}
      <div>
        <div className="mb-1 flex items-center justify-between text-xs">
          <span className="font-semibold text-slate-700">{STATUS_LABEL[job.status] || job.status}{job.currentStep ? ` — ${job.currentStep}` : ''}</span>
          {!isRegenerating && !isAwaitingAdjust && <span className="text-slate-500">{job.progress}%</span>}
        </div>
        {isRegenerating ? (
          /* Regerando: barra indeterminada pulsante (o % antigo não se aplica ao ajuste) */
          <div className="h-3 w-full overflow-hidden rounded-full bg-slate-200">
            <div className="h-full w-1/3 animate-pulse rounded-full bg-blue-500" />
          </div>
        ) : !isAwaitingAdjust && (
          <div className="h-3 w-full overflow-hidden rounded-full bg-slate-200">
            <div className={`h-full rounded-full transition-all ${job.status === 'failed' ? 'bg-rose-500' : 'bg-indigo-600'}`} style={{ width: `${job.progress}%` }} />
          </div>
        )}
        {/* Entrega parcial: o resumo estruturado é mais útil que o job.error corrido,
            então substitui a mensagem quando o backend detalhou o que faltou. */}
        {failures.length > 0 ? (
          <div className="mt-2 rounded-xl border border-amber-200 bg-amber-50 p-3">
            <p className="text-xs font-semibold text-amber-900">
              {summary
                ? `${summary.delivered} de ${summary.requested} foto(s) geradas — ${summary.failed} não saiu(ram).`
                : `${failures.length} etapa(s) não foi(ram) concluída(s).`}
            </p>
            <ul className="mt-1.5 space-y-1.5">
              {failures.map(f => (
                <li key={f.stepId} className="flex items-start justify-between gap-3 text-xs text-amber-800">
                  <span>
                    <span className="font-medium">{f.title}</span>
                    {f.kind === 'seo' ? ' (SEO)' : ''}: {f.message}
                  </span>
                  {f.kind === 'image' && (
                    <button
                      onClick={() => retryStep(f.stepId)}
                      disabled={busy}
                      title="Gera esta foto novamente. Como o crédito dela foi devolvido, uma nova cobrança é feita."
                      className="shrink-0 rounded-lg border border-amber-400 bg-white px-2 py-1 text-[11px] font-semibold text-amber-900 hover:bg-amber-100 disabled:opacity-50"
                    >
                      Tentar novamente
                    </button>
                  )}
                </li>
              ))}
            </ul>
            <p className="mt-1.5 text-[11px] text-amber-700">
              As tentativas automáticas foram esgotadas e os créditos das etapas não entregues foram devolvidos —
              reprocessar cobra o crédito novamente.
            </p>
          </div>
        ) : job.error ? (
          <p className="mt-2 text-xs text-rose-600">{job.error}</p>
        ) : null}
      </div>

      {/* Aprovação de ajuste: imagem atual × nova gerada */}
      {job.status === 'awaiting_adjustment' && pendingRegen && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
          <p className="text-sm font-semibold text-amber-800">Escolha qual imagem fica como oficial</p>
          <p className="mt-0.5 text-xs text-amber-700">Ajuste pedido: "{pendingRegen.feedback}"</p>
          <div className="mt-3 grid grid-cols-2 gap-3">
            {[
              { label: 'Atual', url: pendingRegen.oldUrl, accept: false, btn: 'Manter atual', cls: 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50' },
              { label: 'Nova', url: pendingRegen.newUrl, accept: true, btn: 'Usar a nova', cls: 'bg-indigo-600 text-white hover:bg-indigo-500' },
            ].map(opt => (
              <div key={opt.label} className={`rounded-xl border-2 bg-white p-2 ${opt.accept ? 'border-indigo-400' : 'border-slate-200'}`}>
                <p className="mb-1.5 text-center text-[10px] font-bold uppercase tracking-wider text-slate-500">{opt.label}</p>
                <img src={opt.url} alt={opt.label} className="aspect-square w-full rounded-lg object-cover" />
                <button onClick={() => resolveRegen(opt.accept)} disabled={busy}
                  className={`mt-2 w-full rounded-lg border py-2 text-xs font-bold disabled:opacity-50 ${opt.cls}`}>
                  {opt.btn}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Aprovação da primeira imagem */}
      {job.status === 'awaiting_approval' && firstResult?.imageUrl && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
          <p className="text-sm font-semibold text-amber-800">Aprove a imagem principal para gerar o restante automaticamente</p>
          <img src={firstResult.imageUrl} alt="principal" className="mt-3 max-h-80 rounded-xl border border-amber-200" />
          {/* Regerar aqui também gera uma imagem nova e é cobrado. Aprovar não custa. */}
          <CreditCostNotice
            className="mt-3"
            items={oneImageCost}
            note="Aprovar não custa nada. Regerar gera uma nova imagem, cobrada pelo uso real quando sair — se falhar, não cobra."
          />
          <div className="mt-3 flex gap-2">
            <button onClick={approve} disabled={busy} className="rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">Aprovar e continuar</button>
            <button onClick={regenerate} disabled={busy} className="rounded-xl border border-amber-300 bg-white px-4 py-2 text-sm font-semibold text-amber-700 disabled:opacity-50">Regerar</button>
          </div>
        </div>
      )}

      {/* Galeria de resultados (oculta durante a aprovação de ajuste — só os 2 resultados aparecem) */}
      {!isAwaitingAdjust && images.length > 0 && (
        <div className="flex items-center justify-between">
          <p className="text-sm font-semibold text-slate-700">Imagens geradas{seo.length ? ' (com SEO)' : ''}</p>
          <button onClick={downloadAll} className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50">Baixar todas</button>
        </div>
      )}
      {!isAwaitingAdjust && images.length > 0 && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
          {images.map(img => {
            const s = seoFor(img.stepId);
            const regenerating = isRegenerating && regenTitle === img.title;
            return (
              <div key={img.stepId} className={`overflow-hidden rounded-xl border bg-white shadow-sm ${selected.includes(img.imageUrl!) ? 'border-indigo-500 ring-2 ring-indigo-200' : 'border-slate-200'}`}>
                <div className="relative">
                  <img src={img.imageUrl} alt={img.title} className="aspect-square w-full object-cover cursor-pointer" onClick={() => toggle(img.imageUrl!)} />
                  <input type="checkbox" checked={selected.includes(img.imageUrl!)} onChange={() => toggle(img.imageUrl!)} className="absolute left-2 top-2 h-5 w-5" />
                  {regenerating && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-blue-600/75 backdrop-blur-sm">
                      <span className="h-7 w-7 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                      <span className="text-sm font-bold text-white animate-pulse">Regerando…</span>
                    </div>
                  )}
                </div>
                <div className="p-2">
                  <p className="text-xs font-semibold text-slate-700">{img.title}</p>
                  {s?.titulo_seo && <p className="mt-1 text-[11px] text-slate-500" title={s.titulo_seo}>{s.titulo_seo}</p>}
                  {s?.nome_arquivo && <p className="text-[10px] text-slate-400 truncate">{s.nome_arquivo}</p>}
                  <div className="mt-1">
                    <button onClick={() => downloadImage(img.imageUrl!, s?.nome_arquivo)} className="text-[11px] font-semibold text-indigo-600 hover:underline">Baixar{s?.nome_arquivo ? ' (SEO)' : ''}</button>
                  </div>
                  {/* Botão de regerar logo abaixo de cada imagem */}
                  <button
                    onClick={() => { setRegenStep({ id: img.stepId, title: img.title }); setRegenFeedback(''); setRegenRefUrl(''); }}
                    disabled={job.status !== 'completed'}
                    className="mt-2 w-full rounded-lg border border-amber-300 bg-amber-50 py-1.5 text-[11px] font-semibold text-amber-700 hover:bg-amber-100 disabled:opacity-40">
                    ↻ Regerar imagem
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Aplicar ao produto (apenas para produto do sistema) */}
      {job.status === 'completed' && images.length > 0 && job.productId && (
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <p className="text-sm font-semibold text-slate-800">Aplicar imagens ao produto</p>
          <div className="mt-2 flex flex-wrap items-center gap-4 text-xs text-slate-600">
            <label className="flex items-center gap-2">Modo:
              <select value={mode} onChange={e => setMode(e.target.value as any)} className="rounded-md border border-slate-200 px-2 py-1">
                <option value="replace">Substituir fotos</option>
                <option value="append">Adicionar às fotos</option>
              </select>
            </label>
            {job.productId && (
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={updateMkt} onChange={e => { setUpdateMkt(e.target.checked); if (e.target.checked && !mktTargets.length) loadMarketplaceTargets(); }} />
                Enviar também para outros destinos{destinationNames.length ? ` (${destinationNames.join(', ')})` : ''}
              </label>
            )}
            <span className="text-slate-400">{selected.length ? `${selected.length} selecionada(s)` : 'Todas serão aplicadas'}</span>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <button onClick={apply} disabled={busy} className="rounded-xl bg-slate-950 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
              {mode === 'replace' ? 'Substituir' : 'Adicionar'} {selected.length || images.length} foto{(selected.length || images.length) === 1 ? '' : 's'} no produto
            </button>
            <button onClick={downloadAll} className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Baixar todas as fotos</button>
          </div>
          {updateMkt && (
            <div className="mt-4 border-t border-slate-100 pt-3">
              <div className="mb-2 flex items-center justify-between">
                <p className="text-xs font-semibold text-slate-700">Destinos</p>
                <button onClick={loadMarketplaceTargets} disabled={targetsLoading} className="text-[11px] font-semibold text-indigo-600 hover:underline disabled:opacity-40">{targetsLoading ? 'Carregando...' : 'Atualizar lista'}</button>
              </div>
              {mktTargets.length === 0 && !targetsLoading && <p className="rounded-lg border border-dashed border-slate-200 px-3 py-2 text-xs text-slate-500">Nenhum destino encontrado para este SKU na nova sincronizacao.</p>}
              {marketplaceNodes.length > 0 && hasCutappTarget && <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Anuncios de marketplace</p>}
              <div className="space-y-2">
                {marketplaceNodes.map((target: any) => (
                  <div key={target.id} className="rounded-lg border border-slate-200 bg-slate-50 p-2">
                    <label className="flex items-start gap-2 text-xs text-slate-700">
                      {target.selectable !== false && (target.media || []).includes('image') && <input type="checkbox" checked={selectedTargets.includes(target.id)} onChange={() => toggleTarget(target.id)} />}
                      <span className="min-w-0"><b>{target.platform || target.marketplace}</b> {target.label || target.title || target.listingId}<span className="ml-1 text-slate-400">{target.sku ? `SKU ${target.sku}` : ''}</span><br /><span className="text-[11px] text-slate-500">{target.hint}</span></span>
                    </label>
                    {Array.isArray(target.children) && target.children.length > 0 && (
                      <div className="mt-2 space-y-1 border-l border-slate-200 pl-4">
                        {target.children.map((child: any) => {
                          const blocked = child.type === 'shopee_model_cover';
                          return (
                            <label key={child.id} className={`flex items-start gap-2 text-xs ${blocked ? 'text-slate-400' : 'text-slate-700'}`}>
                              <input type="checkbox" disabled={blocked} checked={selectedTargets.includes(child.id)} onChange={() => toggleTarget(child.id)} />
                              <span className="min-w-0">{child.label || child.title || child.listingId}<span className="ml-1 text-slate-400">{child.sku ? `SKU ${child.sku}` : ''}</span><br /><span className="text-[11px] text-slate-500">{blocked ? 'Capa de variacao Shopee aguardando validacao segura do endpoint.' : child.hint}</span></span>
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {hasCutappTarget && (
                <div className={marketplaceNodes.length > 0 ? 'mt-3' : ''}>
                  <p className="mb-1 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                    <PlatformIcon platform="cutapp" size={14} /> Cut App
                  </p>
                  <div className="space-y-2">
                    {cutappNodes.map((target: any) => (
                      <label key={target.id} className="flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 p-2 text-xs text-slate-700">
                        {target.selectable !== false && (target.media || []).includes('image') && <input type="checkbox" checked={selectedTargets.includes(target.id)} onChange={() => toggleTarget(target.id)} />}
                        <span className="min-w-0">{target.label || target.title || target.listingId}<span className="ml-1 text-slate-400">{target.sku ? `SKU ${target.sku}` : ''}</span><br /><span className="text-[11px] text-slate-500">{target.hint}</span></span>
                      </label>
                    ))}
                  </div>
                  {cutappOverLimit && (
                    <p className="mt-1.5 rounded-lg border border-amber-200 bg-amber-50 px-2 py-1.5 text-[11px] text-amber-800">
                      Você selecionou {imageCount} fotos. O Cut App aceita no máximo {CUTAPP_MAX_PHOTOS} — só as {CUTAPP_MAX_PHOTOS} primeiras irão para lá. Os demais destinos recebem todas.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Modal de regerar imagem */}
      {regenStep && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => !busy && setRegenStep(null)}>
          <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl" onClick={e => e.stopPropagation()}>
            <h3 className="text-base font-bold text-slate-900">Regerar imagem</h3>
            <p className="mt-1 text-xs text-slate-500">{regenStep.title}</p>
            <label className="mt-3 block text-xs font-semibold text-slate-500">O que precisa ser mudado?</label>
            <p className="text-[11px] text-slate-400">A IA recebe a imagem gerada como base e aplica só o seu ajuste.</p>
            <textarea value={regenFeedback} onChange={e => setRegenFeedback(e.target.value)} rows={4} autoFocus
              placeholder="Ex.: deixar o fundo mais claro, aproximar o produto, remover sombra forte..."
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm" />

            {/* Imagem de referência opcional para guiar o ajuste */}
            <label className="mt-3 block text-xs font-semibold text-slate-500">Imagem de referência (opcional)</label>
            <p className="text-[11px] text-slate-400">Ex.: um fundo, material ou composição que você quer como guia do ajuste.</p>
            <div className="mt-1.5 flex items-center gap-2">
              {regenRefUrl ? (
                <div className="relative">
                  <img src={regenRefUrl} alt="referência" className="h-16 w-16 rounded-lg border border-slate-200 object-cover" />
                  <button onClick={() => setRegenRefUrl('')}
                    className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-rose-500 text-[10px] text-white">✕</button>
                </div>
              ) : (
                <label className="flex h-16 w-16 cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 text-center text-[10px] text-slate-400 hover:border-indigo-400">
                  {regenRefUploading ? '...' : '+ foto'}
                  <input type="file" accept="image/*" className="hidden" onChange={e => e.target.files?.[0] && uploadRegenRef(e.target.files[0])} />
                </label>
              )}
            </div>

            {/* Regerar produz uma imagem nova de verdade e é cobrado como uma
                geração. O aviso fica vermelho sozinho se faltar saldo. */}
            <CreditCostNotice
              className="mt-3"
              items={oneImageCost}
              note="Regerar gera uma nova imagem, cobrada pelo uso real quando sair. Se falhar, não cobra."
            />

            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setRegenStep(null)} disabled={busy} className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 disabled:opacity-50">Cancelar</button>
              <button onClick={submitRegen} disabled={busy || !regenFeedback.trim()} className="rounded-xl bg-amber-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">{busy ? 'Enviando...' : 'Regerar'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ─── View principal ──────────────────────────────────────────────────────────
export function EcommercePhotosView({ brand }: { brand: any }) {
  const [tab, setTab] = useState<'gerar' | 'estilos'>('gerar');
  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [selectedPipelineId, setSelectedPipelineId] = useState<string>('');
  const [editPipelineId, setEditPipelineId] = useState<string>('');
  const [editorDirty, setEditorDirty] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState<string>('');
  const [useBrand, setUseBrand] = useState(false);
  const [source, setSource] = useState<ProductSource>('system');
  const [manual, setManual] = useState<ManualProduct>(EMPTY_MANUAL);
  const [manualImageUrl, setManualImageUrl] = useState('');
  const [manualRefs, setManualRefs] = useState<string[]>([]);
  const [activeJob, setActiveJob] = useState<PhotoJob | null>(null);
  const [jobs, setJobs] = useState<PhotoJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  // Passo a passo da geração: 1 Produto → 2 Estilo → 3 Revisar e gerar.
  const [step, setStep] = useState(1);
  const [maxStep, setMaxStep] = useState(1);
  const topRef = useRef<HTMLDivElement>(null);
  const { request: requestPhotoConfirm, modal: photoConfirmModal } = useCreditConfirm();
  const pollRef = useRef<any>(null);

  const load = async () => {
    try {
      const [pRes, prodRes, jobRes] = await Promise.all([
        api.listEcommercePipelines(),
        api.listProducts(),
        api.listEcommercePhotoJobs(),
      ]);
      setPipelines(pRes.pipelines || []);
      setProducts(prodRes.products || []);
      setJobs(jobRes.jobs || []);
      if (pRes.pipelines?.length) {
        setSelectedPipelineId(prev => prev || pRes.pipelines[0].id);
        setEditPipelineId(prev => prev || pRes.pipelines[0].id);
      }
    } catch (e: any) { toast.error(e.message || 'Erro ao carregar'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Polling do job ativo
  useEffect(() => {
    if (!activeJob) return;
    if (['completed', 'failed'].includes(activeJob.status)) return;
    pollRef.current = setInterval(async () => {
      try {
        const res = await api.getEcommercePhotoJob(activeJob.id);
        setActiveJob(res.job);
        if (['completed', 'failed'].includes(res.job.status)) clearInterval(pollRef.current);
      } catch {}
    }, 3000);
    return () => clearInterval(pollRef.current);
  }, [activeJob?.id, activeJob?.status]);

  const selectedPipeline = pipelines.find(p => p.id === selectedPipelineId);
  const pipelineSteps = selectedPipeline?.steps || [];
  const pipelineCostItems = usePipelineCostItems('photo', pipelineSteps);
  const imageSteps = pipelineSteps.filter(s => s.kind === 'image');
  const selectedProduct = products.find(p => p.id === selectedProductId);
  const brandStepTitles = imageSteps.filter(s => s.useBrand).map(s => s.title);

  // O que falta para liberar o botão — mostrado ao lado dele, em vez de toast depois do clique.
  const missing: string[] = [];
  if (source === 'system' && !selectedProductId) missing.push('Escolher um produto');
  if (source === 'manual' && !manualImageUrl) missing.push('Enviar a foto do produto');
  if (source === 'manual' && !manual.name.trim()) missing.push('Informar o nome do produto');
  if (!selectedPipeline) missing.push('Escolher um estilo');
  else if (!imageSteps.length) missing.push('O estilo escolhido não tem etapas de foto');

  const start = async () => {
    if (missing.length) { toast.error(missing[0]); return; }
    setStarting(true);
    try {
      const payload: any = { pipelineId: selectedPipelineId, brandId: brand?.id || null, useBrand };
      if (source === 'manual') payload.manual = { ...manual, imageUrl: manualImageUrl, referenceImages: manualRefs };
      else payload.productId = selectedProductId;
      const res = await api.startEcommercePhotoJob(payload);
      setActiveJob(res.job);
      toast.success('Geração iniciada');
      load();
    } catch (e: any) { toast.error(e.message || 'Erro ao iniciar'); }
    finally { setStarting(false); }
  };

  const confirmLeaveEditor = () => !editorDirty || window.confirm('Há alterações não salvas neste estilo. Descartar?');
  const goTab = (t: 'gerar' | 'estilos') => {
    if (t === tab) return;
    if (tab === 'estilos' && !confirmLeaveEditor()) return;
    setEditorDirty(false);
    setTab(t);
  };
  const openEditor = (id: string) => {
    if (tab === 'estilos' && id !== editPipelineId && !confirmLeaveEditor()) return;
    setEditorDirty(false);
    setEditPipelineId(id);
    setTab('estilos');
  };
  const createPipeline = async () => {
    if (tab === 'estilos' && !confirmLeaveEditor()) return;
    try {
      const res = await api.createEcommercePipeline({ name: 'Meu estilo' });
      toast.success('Estilo criado — adicione as etapas');
      await load();
      setEditorDirty(false);
      setEditPipelineId(res.pipeline.id);
      setTab('estilos');
    } catch (e: any) { toast.error(e.message || 'Erro ao criar'); }
  };

  const editPipeline = pipelines.find(p => p.id === editPipelineId);
  const activeProduct = activeJob?.productId ? products.find(p => p.id === activeJob.productId) : undefined;

  const openJob = async (id: string) => {
    try { const res = await api.getEcommercePhotoJob(id); setActiveJob(res.job); }
    catch (e: any) { toast.error(e.message || 'Erro'); }
  };
  const removeJob = async (id: string) => {
    if (!window.confirm('Remover esta geração?')) return;
    try { await api.deleteEcommercePhotoJob(id); if (activeJob?.id === id) setActiveJob(null); load(); }
    catch (e: any) { toast.error(e.message || 'Erro'); }
  };

  if (loading) return <div className="p-6 text-sm text-slate-500">Carregando...</div>;

  const productDone = source === 'system' ? !!selectedProductId : !!manualImageUrl && !!manual.name.trim();
  const productMissing = missing.filter(m => !m.toLowerCase().includes('estilo'));
  const styleDone = !!selectedPipeline && imageSteps.length > 0;
  // Só dá para pular para um passo cujos anteriores estão completos.
  const reached = Math.min(maxStep, productDone ? (styleDone ? 3 : 2) : 1);
  const goStep = (n: number) => {
    if (n > 1 && !productDone) return;
    if (n > 2 && !styleDone) return;
    setStep(n);
    setMaxStep(m => Math.max(m, n));
    topRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  };
  const summaryThumb = source === 'system' ? (selectedProduct?.imageUrls || [])[0] : manualImageUrl;
  const summaryName = source === 'system' ? selectedProduct?.name : manual.name.trim();

  return (
    <div ref={topRef} className="space-y-5">
      {photoConfirmModal}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-slate-900">Fotos E-commerce</h2>
          <p className="text-sm text-slate-500">Escolha o produto e o estilo. A IA gera as fotos e você aplica no catálogo e nos marketplaces.</p>
        </div>
        <div className="flex rounded-xl bg-slate-100 p-1">
          {([['gerar', 'Gerar fotos'], ['estilos', 'Meus estilos']] as const).map(([t, label]) => (
            <button key={t} type="button" onClick={() => goTab(t)}
              className={`rounded-lg px-4 py-2 text-sm font-semibold transition ${tab === t ? 'bg-slate-950 text-white shadow-sm' : 'text-slate-600 hover:text-slate-950'}`}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {tab === 'estilos' && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            {pipelines.map(p => (
              <button key={p.id} type="button" onClick={() => openEditor(p.id)}
                className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition ${p.id === editPipelineId ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-300 bg-white text-slate-700 hover:border-slate-400'}`}>
                {p.isDefault ? '🔒 ' : ''}{p.name}
              </button>
            ))}
            <button type="button" onClick={createPipeline} className="rounded-full border border-dashed border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:border-indigo-400 hover:text-indigo-600">+ Novo estilo</button>
          </div>
          {editPipeline && (
            <PhotoPipelineFlow
              pipeline={editPipeline}
              onDirtyChange={setEditorDirty}
              onSaved={(p) => setPipelines(list => list.map(x => x.id === p.id ? p : x))}
              onCloned={async (p) => { await load(); setEditorDirty(false); setEditPipelineId(p.id); }}
              onDeleted={async (id) => {
                const fallback = pipelines.find(p => p.id !== id)?.id || '';
                setEditorDirty(false);
                setEditPipelineId(fallback);
                if (selectedPipelineId === id) setSelectedPipelineId(fallback);
                await load();
              }}
            />
          )}
        </div>
      )}

      {tab === 'gerar' && (
        <>
          <WizardHeader steps={['Produto', 'Estilo das fotos', 'Revisar e gerar']} current={step} reached={reached} onGo={goStep} />

          {step === 1 && (
            <div>
              <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
                <h3 className="text-base font-bold text-slate-900">De qual produto são as fotos?</h3>
                <p className="mb-4 text-xs text-slate-500">Escolha do catálogo ou envie a foto de um produto que ainda não está cadastrado.</p>
                <ProductSourcePicker
                  source={source} onSource={setSource}
                  products={products} selectedProductId={selectedProductId} onSelectProduct={setSelectedProductId}
                  manual={manual} onManual={setManual}
                  manualImageUrl={manualImageUrl} onManualImage={setManualImageUrl}
                  manualRefs={manualRefs} onManualRefs={setManualRefs}
                  upload={(fd) => api.uploadEcommerceReference(fd)}
                  refsUsedBy={null}
                  firstAssetLabel="foto principal"
                />
              </section>
              <WizardFooter
                info={productDone && (
                  <div className="flex min-w-0 items-center gap-2">
                    {summaryThumb && <img src={summaryThumb} alt="" className="h-9 w-9 flex-shrink-0 rounded-lg border border-slate-200 object-cover" />}
                    <span className="truncate text-sm font-semibold text-slate-800">{summaryName}</span>
                  </div>
                )}
                onNext={() => goStep(2)} nextLabel="Continuar: escolher estilo →"
                nextDisabled={!productDone} nextHint={productMissing[0]} />
            </div>
          )}

          {step === 2 && (
            <div>
              <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
                <h3 className="text-base font-bold text-slate-900">Qual estilo de fotos?</h3>
                <p className="mb-4 text-xs text-slate-500">Cada estilo é uma sequência de fotos (principal, detalhe, ambientada…). Você pode criar o seu em "Meus estilos".</p>
                <PipelinePicker
                  pipelines={pipelines} selectedId={selectedPipelineId} onSelect={setSelectedPipelineId}
                  stepChip={photoStepChip} estimateKind="photo"
                  onEdit={openEditor} onCreate={createPipeline}
                />
              </section>
              <WizardFooter
                onBack={() => goStep(1)}
                info={selectedPipeline && <span className="truncate text-sm font-semibold text-slate-800">{selectedPipeline.name} · {imageSteps.length} foto{imageSteps.length === 1 ? '' : 's'}</span>}
                onNext={() => goStep(3)} nextLabel="Continuar: revisar →"
                nextDisabled={!styleDone} nextHint={!selectedPipeline ? 'Escolha um estilo' : 'O estilo escolhido não tem etapas de foto'} />
            </div>
          )}

          {step === 3 && (
            <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_360px]">
              <section className="space-y-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
                <h3 className="text-base font-bold text-slate-900">Confira antes de gerar</h3>

                <div className="flex items-center gap-4 rounded-xl border border-slate-200 p-3">
                  {summaryThumb
                    ? <img src={summaryThumb} alt="" className="h-20 w-20 flex-shrink-0 rounded-lg border border-slate-200 object-cover" />
                    : <span className="flex h-20 w-20 flex-shrink-0 items-center justify-center rounded-lg border border-dashed border-slate-300 text-2xl text-slate-300">📦</span>}
                  <div className="min-w-0 flex-1">
                    <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Produto</p>
                    <p className="truncate text-sm font-bold text-slate-900">{summaryName}</p>
                    <p className="text-xs text-slate-500">{source === 'system' ? (selectedProduct?.sku ? `SKU ${selectedProduct.sku}` : 'Do catálogo') : 'Envio manual'}</p>
                  </div>
                  <button type="button" onClick={() => goStep(1)} className="rounded-lg px-2 py-1 text-xs font-semibold text-indigo-600 hover:bg-indigo-50">Trocar</button>
                </div>

                <div className="rounded-xl border border-slate-200 p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Estilo</p>
                      <p className="truncate text-sm font-bold text-slate-900">{selectedPipeline?.name}</p>
                    </div>
                    <button type="button" onClick={() => goStep(2)} className="rounded-lg px-2 py-1 text-xs font-semibold text-indigo-600 hover:bg-indigo-50">Trocar</button>
                  </div>
                  <p className="mt-3 text-xs font-semibold text-slate-600">Você vai receber {imageSteps.length} foto{imageSteps.length === 1 ? '' : 's'}{pipelineSteps.some(s => s.kind === 'seo') ? ' + títulos e nomes de arquivo SEO' : ''}:</p>
                  <ol className="mt-2 grid gap-1.5 sm:grid-cols-2">
                    {imageSteps.map((s, i) => (
                      <li key={s.id} className="flex items-center gap-2 rounded-lg bg-slate-50 px-2 py-1.5 text-xs text-slate-700">
                        <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded bg-indigo-600 text-[10px] font-bold text-white">{i + 1}</span>
                        <span className="truncate">{s.title}</span>
                        {i === 0 && <span className="ml-auto flex-shrink-0 text-[10px] font-semibold text-amber-600">você aprova</span>}
                      </li>
                    ))}
                  </ol>
                  {(() => {
                    const refSteps = imageSteps.filter(s => s.useReference).map(s => s.title);
                    if (source === 'manual' && manualRefs.length && !refSteps.length) {
                      return <p className="mt-2 text-[11px] text-amber-700">As {manualRefs.length} foto(s) de referência que você enviou não são usadas por este estilo.</p>;
                    }
                    if (refSteps.length) return <p className="mt-2 text-[11px] text-slate-500">Fotos de referência entram em: {refSteps.join(', ')}.</p>;
                    return null;
                  })()}
                </div>

                <button type="button" onClick={() => goStep(2)} className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">← Voltar</button>
              </section>

              <aside className="lg:sticky lg:top-4 lg:self-start">
                <div className="space-y-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                  <label className={`flex items-start gap-2.5 rounded-xl border p-2.5 ${brand ? 'border-slate-200' : 'border-slate-100 opacity-60'}`}>
                    <input type="checkbox" checked={useBrand} onChange={e => setUseBrand(e.target.checked)} disabled={!brand} className="mt-0.5 h-4 w-4" />
                    <span>
                      <span className="block text-xs font-semibold text-slate-800">Aplicar minha marca em todas{brand ? ` (${brand.name})` : ''}</span>
                      <span className="block text-[11px] text-slate-500">
                        {!brand ? 'Cadastre uma Brand para usar.'
                          : brandStepTitles.length && !useBrand ? `Já aplicada pelo estilo em: ${brandStepTitles.join(', ')}.`
                          : 'Logo, cores e fontes em cada foto.'}
                      </span>
                    </span>
                  </label>

                  <CreditCostNotice items={pipelineCostItems} note={ESTIMATE_NOTE} />

                  <button type="button" onClick={() => requestPhotoConfirm({
                    title: 'Produzir fotos e-commerce',
                    items: pipelineCostItems,
                    note: ESTIMATE_NOTE,
                    confirmLabel: 'Gerar fotos',
                    onConfirm: start,
                  })} disabled={starting || missing.length > 0}
                    className="w-full rounded-xl bg-indigo-600 px-5 py-3 text-sm font-bold text-white shadow-sm transition hover:bg-indigo-500 disabled:cursor-not-allowed disabled:bg-slate-300">
                    {starting ? 'Iniciando...' : `Gerar ${imageSteps.length} foto${imageSteps.length === 1 ? '' : 's'}`}
                  </button>
                  <p className="text-[11px] text-slate-400">A foto principal sai primeiro para você aprovar; as demais são geradas em seguida, automaticamente.</p>
                </div>
              </aside>
            </div>
          )}

          <GenerationsGallery jobs={jobs} products={products} onOpen={openJob} onRemove={removeJob} unit="fotos" />
        </>
      )}

      {/* Detalhe da geração em painel lateral */}
      {activeJob && (
        <div className="fixed inset-0 z-40 flex justify-end bg-black/40" onClick={() => setActiveJob(null)}>
          <div className="h-full w-full max-w-3xl overflow-y-auto bg-white p-5 shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="mb-4 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <h3 className="truncate text-base font-bold text-slate-900">{activeJob.input?.product?.name || activeProduct?.name || 'Geração de fotos'}</h3>
                <p className="text-xs text-slate-500">{STATUS_LABEL[activeJob.status] || activeJob.status} · {activeJob.progress}%</p>
              </div>
              <button type="button" onClick={() => setActiveJob(null)} className="shrink-0 rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50">Fechar</button>
            </div>
            <JobRunner job={activeJob} product={activeProduct}
              onChanged={async () => { const res = await api.getEcommercePhotoJob(activeJob.id); setActiveJob(res.job); load(); }} />
          </div>
        </div>
      )}
    </div>
  );
}
